// =============================================================================
// FILE: packages/renderer/src/components/SystemHealthBanner.tsx
// PURPOSE: Non-blocking startup banner that probes the main process for the
//          set of IPC channels the UI depends on (post-portability
//          stabilisation requirement). If any required channel is missing a
//          handler, the banner stays visible and lists the missing channels
//          so the user / QA can act.
// =============================================================================

import { useEffect, useState } from 'react';
import { call, isElectron } from '../lib/api.js';
import { IPC } from '@bim/shared/constants/ipc.constants.js';

interface ProbeResult {
  checkedAt: string;
  checked:   { channel: string; registered: boolean }[];
  missing:   string[];
}

// Channels that MUST be wired for the portable shell to be considered healthy.
const REQUIRED_CHANNELS: string[] = [
  IPC.PROJECT_CREATE,
  IPC.PROJECT_GET,
  IPC.PROJECT_LIST,
  IPC.SYSTEM_RESET_WORKSPACE,
  IPC.SYSTEM_PORTABLE_INFO,
];

export function SystemHealthBanner(): JSX.Element | null {
  const [probe, setProbe] = useState<ProbeResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try { return sessionStorage.getItem('bim.health.dismissed') === '1'; } catch { return false; }
  });

  useEffect(() => {
    // In web-preview mode we can't probe the main process at all; render
    // a soft notice rather than a hard warning. The mock IPC layer will
    // return success for every channel below, so the banner stays hidden.
    if (!isElectron()) {
      setProbe({ checkedAt: new Date().toISOString(),
        checked: REQUIRED_CHANNELS.map((c) => ({ channel: c, registered: true })),
        missing: [] });
      return;
    }

    void call<{ channels: string[] }, ProbeResult>(
      IPC.SYSTEM_BOOT_PROBE,
      { channels: REQUIRED_CHANNELS },
    )
      .then(setProbe)
      .catch((err: Error) => {
        // If the probe channel itself isn't registered, everything we care
        // about is missing — surface that clearly.
        setError(err.message);
        setProbe({
          checkedAt: new Date().toISOString(),
          checked:   REQUIRED_CHANNELS.map((c) => ({ channel: c, registered: false })),
          missing:   [...REQUIRED_CHANNELS, IPC.SYSTEM_BOOT_PROBE],
        });
      });
  }, []);

  if (!probe) return null;
  if (probe.missing.length === 0) return null;
  if (dismissed) return null;

  return (
    <div
      data-testid="system-health-banner"
      role="status"
      aria-live="polite"
      className="bg-amber-500/10 border-b border-amber-500/40 px-6 py-2.5 text-[12px] text-amber-200 flex items-center gap-4"
    >
      <span
        data-testid="system-health-banner-title"
        className="font-semibold uppercase tracking-[0.18em] text-amber-300 shrink-0">
        System Not Fully Initialised
      </span>
      <span className="text-amber-200/90">
        Missing IPC handler{probe.missing.length === 1 ? '' : 's'}:{' '}
        <code
          data-testid="system-health-banner-missing"
          className="font-mono text-amber-100">
          {probe.missing.join(', ')}
        </code>
      </span>
      {error && (
        <span className="text-amber-300/70 text-[11px]">({error})</span>
      )}
      <button
        type="button"
        data-testid="system-health-banner-dismiss"
        onClick={() => {
          setDismissed(true);
          try { sessionStorage.setItem('bim.health.dismissed', '1'); } catch { /* noop */ }
        }}
        className="ml-auto text-[11px] uppercase tracking-[0.18em] text-amber-300/70 hover:text-amber-200"
      >
        Dismiss
      </button>
    </div>
  );
}
